import { FaBook } from "react-icons/fa";
import { FiClock } from "react-icons/fi";
import type { TFunction } from "i18next";
import { eBoekhoudenStateLabel, useEBoekhoudenStatus } from "../../../components/eboekhouden/EBoekhoudenStatusDot";
import { formatCompactDateTime } from "../utils/dashboardDate.utils";
import { healthBadgeClass } from "../utils/dashboardStatus.utils";
import type { IntegrationHealthStatus } from "../types/dashboard.types";

interface EBoekhoudenActionTileProps {
  locale: string;
  onClick: () => void;
  t: TFunction;
}

/** Status of the e-Boekhouden connection among the open actions, like the tiles for Gitea, Noctis and Mailchimp. */
const EBoekhoudenActionTile = ({ locale, onClick, t }: EBoekhoudenActionTileProps) => {
  const { checking, state, data } = useEBoekhoudenStatus(true);
  const health: IntegrationHealthStatus = checking || !state
    ? "warning"
    : state === "OK" ? "online" : state === "DISABLED" || state === "NOT_CONFIGURED" ? "disabled" : "error";
  const checked = data?.checkedAt ? formatCompactDateTime(data.checkedAt, locale) : "";
  const tf = t as unknown as (key: string, options?: Record<string, unknown>) => string;

  return (
    <div className="rounded-[24px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_12px_30px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-950/60" data-eboekhouden-tile={state ?? "CHECKING"}>
      <div className="flex flex-col gap-4 xl:flex-row xl:items-stretch xl:justify-between">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <FaBook className="text-2xl text-emerald-600" />
            <span>e-Boekhouden</span>
          </p>
          <p className="mt-2 text-xl font-semibold text-slate-900 dark:text-white">{eBoekhoudenStateLabel(tf, state, checking)}</p>
          {checked && <p className="mt-3 text-sm text-slate-700 dark:text-slate-200">{checked}</p>}
        </div>
        <div className="flex min-w-0 flex-col items-end justify-between gap-3 xl:min-w-[140px]">
          {checked && (
            <div className="ml-auto w-full text-right text-xs font-semibold text-slate-500 dark:text-slate-300">
              <FiClock className="mr-1 inline" />
              {checked}
            </div>
          )}
          <button
            type="button"
            onClick={onClick}
            className="inline-flex max-w-full shrink items-center justify-center rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:-translate-y-0.5 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 motion-reduce:transform-none self-stretch xl:self-auto"
          >
            {t("common.open", { defaultValue: "Open" })}
          </button>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${healthBadgeClass(health)}`}>
            {health === "online"
              ? t("status.online", { defaultValue: "Online" })
              : health === "warning"
                ? t("status.warning", { defaultValue: "Warning" })
                : health === "disabled"
                  ? t("status.disabled", { defaultValue: "Disabled" })
                  : t("status.error", { defaultValue: "Error" })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default EBoekhoudenActionTile;
